/**
 * MFX OS — Job Ticket page (blueprint Stages 6-7): the execution hub.
 * Jobs with per-gate green-light boards; blocked gates carry a reason and
 * overrides demand reason + risk note (recorded to the audit trail).
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOsDoc, useOsList, fmtDate } from '../../os/core';
import { updateReadinessGate, type GatePatch } from '../../os/commands';
import { Chip, GateBoard, Lineage, Timeline } from '../../os/ui';
import type { GateCode, JobTicketOS, ProductionJob } from '../../os/types';

export default function TicketPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const id = sp.get('id') || '';
  const { data: jt, loading } = useOsDoc<JobTicketOS>('jobTickets', id);
  const { rows: jobs } = useOsList<ProductionJob>('productionJobs', { wheres: [['jobTicketId', '==', id]] }, [id]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<'jobs' | 'activity'>('jobs');

  if (loading) return <div className="loading"><div className="spinner" /> Loading ticket…</div>;
  if (!jt) return <div className="empty">Ticket not found.</div>;

  const sorted = [...jobs].sort((a, b) => a.displayId.localeCompare(b.displayId));
  const blockers = sorted.flatMap((j) => j.gates.filter((g) => g.status === 'blocked').map((g) => ({ job: j.displayId, gate: g.label, reason: g.blockerReason || '' })));

  const patch = async (job: ProductionJob & { id: string }, code: GateCode, p: GatePatch) => {
    setBusy(true); setErr('');
    try { await updateReadinessGate(job, code, p); } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };

  return (
    <>
      <Lineage parts={[
        { label: jt.customerName || 'Client' },
        { label: jt.soNum, to: `/os/so?id=${jt.salesOrderId}` },
        { label: jt.displayId },
      ]} />

      <div className="page-head">
        <div>
          <div className="page-title">{jt.displayId} <Chip label={jt.status} /></div>
          <div className="page-sub">{jt.customerName} · {sorted.length} production job(s) · due {fmtDate(jt.dueDate)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="metric-label">Readiness</div>
          <div className="metric-value" style={{ color: (jt.readinessPct || 0) === 100 ? 'var(--gn)' : jt.blockedCount ? 'var(--rd)' : 'var(--yl)' }}>{jt.readinessPct || 0}%</div>
          <div className="metric-note">{jt.blockedCount ? `${jt.blockedCount} blocker(s)` : (jt.readinessPct || 0) === 100 ? 'all green lights' : 'gates in progress'}</div>
        </div>
      </div>

      {err && <div className="os-err">{err}</div>}
      {blockers.length > 0 && (
        <div className="os-blockers" style={{ marginBottom: 14 }}>
          <div className="os-blockers-title">⛔ Blocking right now</div>
          {blockers.map((b, i) => <div key={i} className="os-blocker">• {b.job} — {b.gate}: {b.reason}</div>)}
        </div>
      )}

      <div className="seg" style={{ marginBottom: 14 }}>
        <button className={tab === 'jobs' ? 'on' : ''} onClick={() => setTab('jobs')}>Production jobs + green lights</button>
        <button className={tab === 'activity' ? 'on' : ''} onClick={() => setTab('activity')}>Activity</button>
      </div>

      {tab === 'jobs' && sorted.map((j) => (
        <div key={j.id} className="card os-card" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <strong>{j.displayId}</strong> — {j.description} × {j.qty.toLocaleString()}
              <span style={{ color: 'var(--tx2)', marginLeft: 8 }}>{j.internalPart || 'no item'}{j.blueprintRevCode ? ` · Blueprint Rev ${j.blueprintRevCode}` : ''}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Chip label={j.readiness} />
              <Chip label={j.status} />
            </div>
          </div>
          <GateBoard gates={j.gates} busy={busy} onPatch={(code, p) => patch(j, code, p)} />
          {j.readiness === 'ready' && <div className="os-clear" style={{ marginTop: 10 }}>🟢 All required gates green — schedulable (production runs arrive in Release 5).</div>}
        </div>
      ))}
      {tab === 'jobs' && !sorted.length && <div className="empty">No production jobs on this ticket.</div>}

      {tab === 'activity' && (
        <div className="card os-card">
          <div className="os-sect" style={{ marginTop: 0 }}>Ticket</div>
          <Timeline entityId={jt.id} />
          {sorted.map((j) => (
            <div key={j.id}>
              <div className="os-sect">{j.displayId}</div>
              <Timeline entityId={j.id} />
            </div>
          ))}
        </div>
      )}

      <div className="page-sub" style={{ marginTop: 6 }}>
        <button className="btn sm" onClick={() => nav(`/os/so?id=${jt.salesOrderId}`)}>← {jt.soNum}</button>
      </div>
    </>
  );
}
